import { IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED } from '@/app-shell/flags/evidence-flags';
import { useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { parseTimeStamp } from '@heidi/utils';
import { differenceInCalendarDays, differenceInHours, isPast } from 'date-fns';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useQueryUser } from './query/useQueryUser';
import { useQueryUserBillingInfo } from './query/useQueryUserBillingInfo';

const useReverseTrial = () => {
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { data: freeTrialHistoryTiers, isSuccess: isUserQuerySuccess } = useQueryUser(
    (user) => user?.free_trial_history_tiers
  );
  const { t } = useTranslations();
  const isInReverseTrial = Boolean(billingInfo?.is_in_reverse_free_trial);
  const isInFreeTrial = Boolean(billingInfo?.is_in_free_trial);
  const isEvidenceInFreeTrial = Boolean(billingInfo?.evidence_billing?.is_in_free_trial);
  const reverseTrialTier = billingInfo?.tier;
  const isPracticeTrialUser =
    isInReverseTrial && reverseTrialTier === SubscriptionTier.PRO_PLUS;
  const isProTrialUser = isInReverseTrial && reverseTrialTier === SubscriptionTier.PRO;
  const trialDaysLeft = billingInfo?.trialDaysLeft ?? 1;
  const trialHoursLeft = billingInfo?.trialHoursLeft ?? 0;
  const showHoursInsteadOfDays =
    isInReverseTrial && trialHoursLeft > 0 && trialHoursLeft < 24;
  const showReverseModal = true;
  const isLaunchTrialEnabled = !!IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();

  // reverse trials for new products - Clinician, Clinician Practice
  const isFakeClinicianTrial = Boolean(billingInfo?.is_fake_clinician_trial);
  const newProductTier = billingInfo?.new_product_tier;
  // Scribe Team (legacy PRO_PLUS) — practice upgrade target half of
  // isPracticeUpgradeTarget in resolveEvidenceUpgradeModalTarget callers.
  const isScribeTeamUser = newProductTier === SubscriptionTier.PRO_PLUS;
  const isClinicianTrialUser =
    isLaunchTrialEnabled &&
    (isInReverseTrial || isFakeClinicianTrial) &&
    newProductTier === SubscriptionTier.CLINICIAN_PRO;
  const isClinicianPracticeTrialUser =
    isLaunchTrialEnabled &&
    (isInReverseTrial || isFakeClinicianTrial) &&
    newProductTier === SubscriptionTier.CLINICIAN_PRO_PLUS;
  const isEligibleForPracticeTrial =
    isLaunchTrialEnabled &&
    (isInReverseTrial ||
      isInFreeTrial ||
      (isEvidenceInFreeTrial &&
        newProductTier === SubscriptionTier.CLINICIAN_PRO_PLUS)) &&
    reverseTrialTier === SubscriptionTier.PRO_PLUS;
  const isRealStripeClinicianUser =
    (newProductTier === SubscriptionTier.CLINICIAN_PRO ||
      newProductTier === SubscriptionTier.CLINICIAN_PRO_PLUS) &&
    !isFakeClinicianTrial;
  const isEvidenceTrialUser =
    newProductTier === SubscriptionTier.EVIDENCE &&
    Boolean(billingInfo?.evidence_billing?.is_in_free_trial);

  const evidenceTrialDaysLeft = useMemo(() => {
    const trialEndDate = billingInfo?.evidence_billing?.trial_end_date;
    if (!trialEndDate) return 0;
    return Math.max(
      0,
      differenceInCalendarDays(parseTimeStamp(trialEndDate), new Date())
    );
  }, [billingInfo?.evidence_billing?.trial_end_date]);

  const previousStatusBeforeClinicianTrial =
    billingInfo?.previous_status_before_clinician_trial;

  const clinicianTrialEndDate = billingInfo?.evidence_billing?.trial_end_date;

  const clinicianTrialEnded = useMemo(() => {
    return (
      (!clinicianTrialEndDate || isPast(parseTimeStamp(clinicianTrialEndDate))) &&
      !!previousStatusBeforeClinicianTrial
    );
  }, [clinicianTrialEndDate, previousStatusBeforeClinicianTrial]);

  const clinicianTrialDaysLeft = useMemo(() => {
    if (!clinicianTrialEndDate) return 0;
    return Math.max(
      0,
      differenceInCalendarDays(parseTimeStamp(clinicianTrialEndDate), new Date())
    );
  }, [clinicianTrialEndDate]);

  const clinicianTrialHoursLeft = useMemo(() => {
    if (!clinicianTrialEndDate) return 0;
    const hours = differenceInHours(parseTimeStamp(clinicianTrialEndDate), new Date());
    return Math.max(0, hours);
  }, [clinicianTrialEndDate]);

  const daysToast = t('trialToPaidModal.proActionDaysDesc', { days: trialDaysLeft });
  const hoursToast = t('trialToPaidModal.proActionHoursDesc', { hours: trialHoursLeft });
  const reverseTrialToastDescription = useMemo(
    () => (showHoursInsteadOfDays ? hoursToast : daysToast),
    [showHoursInsteadOfDays, hoursToast, daysToast]
  );

  const reverseTrialTitle = useMemo(() => {
    if (trialHoursLeft <= 1) {
      return t('trialToPaidModal.lessThanOneHourRemaining');
    }
    if (showHoursInsteadOfDays) {
      return t('trialToPaidModal.hoursRemaining', { hours: trialHoursLeft });
    }
    if (trialDaysLeft <= 2) {
      return t('trialToPaidModal.onlyDaysRemaining', { days: trialDaysLeft });
    }
    return t('trialToPaidModal.daysRemaining', { days: trialDaysLeft });
  }, [trialDaysLeft, trialHoursLeft, showHoursInsteadOfDays, t]);

  const reverseTrialLostAccessText = useMemo(() => {
    return trialHoursLeft <= 1
      ? t('trialToPaidModal.loseAccessLessThanOneHour')
      : showHoursInsteadOfDays
        ? t('trialToPaidModal.loseAccessHours', { hours: trialHoursLeft })
        : t('trialToPaidModal.loseAccessDays', { days: trialDaysLeft });
  }, [trialDaysLeft, trialHoursLeft, showHoursInsteadOfDays, t]);

  const clinicianTrialAvailable = useMemo(() => {
    if (!isUserQuerySuccess) return false;
    if (isEmpty(freeTrialHistoryTiers)) return true;
    return !freeTrialHistoryTiers.some(
      (tier) =>
        tier === SubscriptionTier.CLINICIAN_PRO ||
        tier === SubscriptionTier.CLINICIAN_PRO_PLUS
    );
  }, [freeTrialHistoryTiers, isUserQuerySuccess]);

  const returnValue = useMemo(
    () => ({
      isFakeClinicianTrial,
      isInReverseTrial,
      reverseTrialTier,
      isPracticeTrialUser,
      isEligibleForPracticeTrial,
      isProTrialUser,
      trialDaysLeft,
      trialHoursLeft,
      showHoursInsteadOfDays,
      showReverseModal,
      reverseTrialTitle,
      reverseTrialLostAccessText,
      reverseTrialToastDescription,
      clinicianTrialEnded,
      isClinicianTrialUser,
      isClinicianPracticeTrialUser,
      isScribeTeamUser,
      clinicianTrialDaysLeft,
      clinicianTrialHoursLeft,
      previousStatusBeforeClinicianTrial,
      isRealStripeClinicianUser,
      isEvidenceTrialUser,
      evidenceTrialDaysLeft,
      isEvidenceInFreeTrial,
      clinicianTrialAvailable,
      heidiVersion: billingInfo?.heidi_version,
      newProductTier,
      seatType: billingInfo?.seat_type,
    }),
    [
      isFakeClinicianTrial,
      isInReverseTrial,
      reverseTrialTier,
      isPracticeTrialUser,
      isEligibleForPracticeTrial,
      isProTrialUser,
      trialDaysLeft,
      trialHoursLeft,
      showHoursInsteadOfDays,
      showReverseModal,
      reverseTrialTitle,
      reverseTrialLostAccessText,
      reverseTrialToastDescription,
      clinicianTrialEnded,
      isClinicianTrialUser,
      isClinicianPracticeTrialUser,
      isScribeTeamUser,
      clinicianTrialDaysLeft,
      clinicianTrialHoursLeft,
      previousStatusBeforeClinicianTrial,
      isRealStripeClinicianUser,
      isEvidenceTrialUser,
      evidenceTrialDaysLeft,
      isEvidenceInFreeTrial,
      clinicianTrialAvailable,
      billingInfo?.heidi_version,
      newProductTier,
      billingInfo?.seat_type,
    ]
  );

  return returnValue;
};

export default useReverseTrial;
